import React from 'react';
import PixelIcons from '../components/PixelIcons.jsx';
import { useT } from '../i18n/useT.js';

// Shop.jsx — Digital goods store with working cart
const Shop = (() => {
  const { Cart, Coin, Diamond, Joystick, Star, Heart, Bolt } = PixelIcons;

  const items = [
    { k: "i1", code: "SP-001", price: 12, icon: Joystick, accent: "var(--ragross-magenta)", tag: "BEST" },
    { k: "i2", code: "UI-002", price: 28, icon: Diamond,  accent: "var(--ragross-cyan)",    tag: "NEW" },
    { k: "i3", code: "TL-003", price: 18, icon: Star,     accent: "var(--ragross-gold)",    tag: "PACK" },
    { k: "i4", code: "SD-004", price: 9,  icon: Bolt,     accent: "var(--ragross-green)",   tag: "WAV" },
    { k: "i5", code: "CR-005", price: 49, icon: Coin,     accent: "var(--ragross-magenta)", tag: "COURSE" },
    { k: "i6", code: "PR-006", price: 24, icon: Heart,    accent: "var(--ragross-cyan)",    tag: "PHYSICAL" },
  ];

  const shopStyles = {
    section: {
      padding: "112px 0",
      background:
        "linear-gradient(180deg, rgba(57,34,110,0.18) 0%, rgba(11,0,20,0.0) 100%), var(--bg-app)",
    },
    container: { maxWidth: 1280, margin: "0 auto", padding: "0 32px" },
    head: {
      display: "flex", justifyContent: "space-between", alignItems: "flex-end",
      flexWrap: "wrap", gap: 24, marginBottom: 56,
    },
    headLeft: { display: "flex", flexDirection: "column", gap: 14, maxWidth: 640 },
    tag: {
      display: "inline-flex", alignItems: "center", gap: 12,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "11px", letterSpacing: "0.16em",
      color: "var(--ragross-magenta)",
    },
    title: { font: "var(--type-display-lg)", letterSpacing: "-0.015em", lineHeight: 1.05, color: "#fff", margin: 0 },
    lead: { font: "var(--type-body-lg)", color: "var(--fg-2)", maxWidth: 560, margin: 0 },

    cartPanel: {
      display: "flex", alignItems: "center", gap: 14,
      padding: "12px 16px",
      background: "var(--ragross-purple-night)",
      border: "2px solid var(--ragross-cyan)",
      boxShadow: "var(--shadow-pixel)",
      borderRadius: "var(--radius-card)",
      minWidth: 280,
    },
    cartLabel: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 9, letterSpacing: "0.14em",
      color: "var(--ragross-cyan)",
    },
    cartCount: {
      fontFamily: "'VT323', monospace",
      fontSize: 28, color: "#fff", lineHeight: 1,
    },
    cartTotal: {
      marginLeft: "auto",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 12, color: "var(--ragross-gold)",
    },

    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 18,
    },
    card: {
      position: "relative",
      background: "var(--bg-surface)",
      border: "1px solid var(--border-soft)",
      borderRadius: "var(--radius-card)",
      overflow: "hidden",
      display: "flex", flexDirection: "column",
      transition: "transform 200ms var(--ease-arcade), box-shadow 200ms, border-color 200ms",
    },
    thumb: (accent) => ({
      aspectRatio: "1.6/1",
      background:
        `radial-gradient(circle at 30% 30%, ${accent === "var(--ragross-magenta)" ? "rgba(253,20,145,0.40)" : accent === "var(--ragross-cyan)" ? "rgba(0,232,203,0.40)" : accent === "var(--ragross-gold)" ? "rgba(253,177,21,0.40)" : "rgba(79,253,21,0.40)"} 0%, transparent 60%), var(--bg-screen)`,
      position: "relative",
      display: "flex", alignItems: "center", justifyContent: "center",
      borderBottom: `2px solid ${accent}`,
    }),
    thumbScan: {
      position: "absolute", inset: 0,
      background: "var(--scanlines)", opacity: 0.5,
      pointerEvents: "none",
    },
    chipTag: (accent) => ({
      position: "absolute", top: 8, left: 8,
      padding: "4px 7px",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.10em",
      color: accent, border: `1px solid ${accent}`,
      background: "rgba(11,0,20,0.85)",
      borderRadius: 2,
    }),
    code: {
      position: "absolute", top: 8, right: 8,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.10em",
      color: "var(--fg-3)",
    },
    body: { padding: 16, display: "flex", flexDirection: "column", gap: 10, flex: 1 },
    name: { font: "var(--type-h3)", color: "#fff", margin: 0 },
    desc: { font: "var(--type-body-sm)", color: "var(--fg-2)", margin: 0, flex: 1 },
    footer: {
      padding: "12px 16px",
      borderTop: "1px solid var(--border-soft)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    },
    price: {
      fontFamily: "'VT323', monospace",
      fontSize: 24, color: "var(--ragross-gold)",
      lineHeight: 1, display: "inline-flex", alignItems: "baseline", gap: 4,
    },
    priceUnit: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 9, color: "var(--fg-3)",
    },
  };

  function ShopCard({ item, added, onAdd }) {
    const t = useT();
    const [hover, setHover] = React.useState(false);
    const Icon = item.icon;
    return (
      <article
        style={{
          ...shopStyles.card,
          borderColor: hover ? item.accent : shopStyles.card.borderColor,
          transform: hover ? "translate(-2px,-2px)" : "none",
          boxShadow: hover ? `4px 4px 0 ${item.accent}` : "var(--shadow-card)",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div style={shopStyles.thumb(item.accent)}>
          <Icon size={64}/>
          <span style={shopStyles.chipTag(item.accent)}>▸ {item.tag}</span>
          <span style={shopStyles.code}>{item.code}</span>
          <div style={shopStyles.thumbScan}></div>
        </div>
        <div style={shopStyles.body}>
          <h3 style={shopStyles.name}>{t(`shop.${item.k}`)}</h3>
          <p style={shopStyles.desc}>{t(`shop.${item.k}d`)}</p>
        </div>
        <div style={shopStyles.footer}>
          <span style={shopStyles.price}>
            ${item.price}
            <span style={shopStyles.priceUnit}>USD</span>
          </span>
          <button
            className={added ? "btn btn-secondary" : "btn btn-primary"}
            style={{ padding: "10px 14px", fontSize: 9 }}
            onClick={onAdd}
          >
            <Cart size={11}/>
            <span>{added ? t("shop.added") : t("shop.add")}</span>
          </button>
        </div>
      </article>
    );
  }

  function Shop() {
    const t = useT();
    const [cart, setCart] = React.useState(new Set());

    const add = (k) => {
      setCart((prev) => {
        const next = new Set(prev);
        if (next.has(k)) next.delete(k); else next.add(k);
        return next;
      });
    };

    const total = items.filter((i) => cart.has(i.k)).reduce((s, i) => s + i.price, 0);

    return (
      <section id="shop" style={shopStyles.section} data-screen-label="07 Tienda">
        <div style={shopStyles.container}>
          <div style={shopStyles.head}>
            <div style={shopStyles.headLeft}>
              <div style={shopStyles.tag}>
                <span>{t("shop.tag")}</span>
                <span style={{ width: 60, height: 1, background: "linear-gradient(90deg, var(--ragross-magenta), transparent)" }}></span>
              </div>
              <h2 style={shopStyles.title} className="section-title">
                {t("shop.title1")} <span style={{ color: "var(--ragross-gold)" }}>{t("shop.title2")}</span>
              </h2>
              <p style={shopStyles.lead}>{t("shop.lead")}</p>
            </div>

            <div style={shopStyles.cartPanel}>
              <Cart size={28}/>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={shopStyles.cartLabel}>{t("shop.cart")}</span>
                <span style={shopStyles.cartCount}>
                  {cart.size} <span style={{ fontSize: 12, color: "var(--fg-3)" }}>{t("shop.items")}</span>
                </span>
              </div>
              <span style={shopStyles.cartTotal}>${total}</span>
            </div>
          </div>

          <div style={shopStyles.grid} className="responsive-grid-3">
            {items.map((it) => (
              <ShopCard key={it.k} item={it} added={cart.has(it.k)} onAdd={() => add(it.k)}/>
            ))}
          </div>

          <div style={{ marginTop: 32, textAlign: "center" }}>
            <button
              className="btn btn-gold"
              disabled={cart.size === 0}
              style={{ opacity: cart.size === 0 ? 0.4 : 1 }}
            >
              <Coin size={12}/>
              <span>{cart.size === 0 ? t("shop.empty") : `${t("shop.checkout")} · $${total}`}</span>
            </button>
          </div>
        </div>
      </section>
    );
  }

  return Shop;
})();

export default Shop;
